import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getPartners, createPartner, updatePartner, deletePartner, uploadImage, getFileUrl, getWorkPackages } from '../../services/apiService';

const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [logoType, setLogoType] = useState('link'); // 'link' or 'upload'
  const [uploadedLogo, setUploadedLogo] = useState(null); // File object
  const [uploadedLogoPath, setUploadedLogoPath] = useState(null); // Uploaded file path
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [workPackages, setWorkPackages] = useState([]); // Liste des work packages disponibles
  const [workPackagesLead, setWorkPackagesLead] = useState([]); // Work packages où le partenaire est lead
  const [workPackagesContributing, setWorkPackagesContributing] = useState([]); // Work packages où le partenaire contribue
  const [newWPId, setNewWPId] = useState(''); // Nouveau WP ID
  const [newWPTitle, setNewWPTitle] = useState(''); // Nouveau WP Title
  const [newWPLead, setNewWPLead] = useState(''); // Nouveau WP Lead
  const [newWPType, setNewWPType] = useState('Lead'); // 'Lead' ou 'Contributing partner'
  const [editingWPIndex, setEditingWPIndex] = useState(null); // Index du WP en cours d'édition
  const [editingWPType, setEditingWPType] = useState(null); // 'Lead' ou 'Contributing'

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchPartners = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getPartners();
      setPartners(data || []);
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement des partenaires');
      setPartners([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');

    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }

    fetchPartners();
    fetchWorkPackages();
  }, [navigate]);

  // Fetch work packages for dropdown
  const fetchWorkPackages = async () => {
    try {
      const data = await getWorkPackages();
      setWorkPackages(data || []);
    } catch (err) {
      console.error('Error fetching work packages:', err);
    }
  };

  const filteredPartners = partners.filter((p) => {
    const matchesSearch = !searchTerm || 
      (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.fullName && p.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.country && p.country.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCountry = !countryFilter || (p.country && p.country === countryFilter);
    
    return matchesSearch && matchesCountry;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPartners = filteredPartners.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPartners.length / itemsPerPage) || 1;

  // Get unique countries for filter
  const uniqueCountries = [...new Set(partners.map(p => p.country).filter(Boolean))].sort();

  const handleNew = () => {
    setEditingPartner(null);
    setLogoType('link');
    setUploadedLogo(null);
    setUploadedLogoPath(null);
    setLogoError(null);
    setWorkPackagesLead([]);
    setWorkPackagesContributing([]);
    setNewWPId('');
    setNewWPTitle('');
    setNewWPLead('');
    setNewWPType('Lead');
    setEditingWPIndex(null);
    setEditingWPType(null);
    setShowForm(true);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    // Déterminer le type de logo (URL ou upload)
    if (partner.logoUrl) {
      const isUrl = partner.logoUrl.startsWith('http://') || partner.logoUrl.startsWith('https://');
      setLogoType(isUrl ? 'link' : 'upload');
      if (!isUrl) {
        setUploadedLogoPath(partner.logoUrl);
      }
    } else {
      setLogoType('link');
    }
    setUploadedLogo(null);
    setLogoError(null);
    // Initialiser les work packages
    setWorkPackagesLead(partner.workPackagesLead || []);
    setWorkPackagesContributing(partner.workPackagesContributing || []);
    setNewWPId('');
    setNewWPTitle('');
    setNewWPLead('');
    setNewWPType('Lead');
    setEditingWPIndex(null);
    setEditingWPType(null);
    setShowForm(true);
  };

  const handleDelete = (partner) => {
    setPartnerToDelete(partner);
  };

  const confirmDeletePartner = async () => {
    if (partnerToDelete) {
      try {
        await deletePartner(partnerToDelete.id);
        showNotification('Partenaire supprimé avec succès', 'success');
        setPartnerToDelete(null);
        fetchPartners();
      } catch (err) {
        console.error(err);
        showNotification('Erreur lors de la suppression du partenaire', 'error');
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier (images uniquement)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Please upload an image file only (JPEG, PNG, GIF, WebP, or SVG).');
      e.target.value = '';
      setUploadedLogo(null);
      return;
    }

    // Vérifier la taille (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setLogoError(`File size exceeds the maximum allowed size of 5MB. Your file is ${fileSizeMB}MB.`);
      e.target.value = '';
      setUploadedLogo(null);
      return;
    }

    setUploadedLogo(file);
    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      const uploaded = await uploadImage(file);
      if (uploaded && uploaded.path) {
        setUploadedLogoPath(uploaded.path);
        setLogoError(null);
      } else {
        throw new Error('Upload failed - no path returned');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError('Error uploading logo. Please try again.');
      setUploadedLogo(null);
      setUploadedLogoPath(null);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Si un logo est en cours d'upload, attendre
    if (uploadedLogo && isUploadingLogo) {
      return;
    }

    const form = e.target;
    const formData = new FormData(form);

    let logoUrl = null;
    
    // Gérer le logo selon le type
    if (logoType === 'upload') {
      if (uploadedLogoPath) {
        // Logo déjà uploadé
        logoUrl = uploadedLogoPath;
      } else if (uploadedLogo) {
        // Uploader le logo maintenant
        try {
          setIsUploadingLogo(true);
          const uploaded = await uploadImage(uploadedLogo);
          if (uploaded && uploaded.path) {
            logoUrl = uploaded.path;
          } else {
            throw new Error('Upload failed - no path returned');
          }
        } catch (error) {
          showNotification('Error uploading logo: ' + (error.message || 'Unknown error'), 'error');
          setIsUploadingLogo(false);
          return;
        } finally {
          setIsUploadingLogo(false);
        }
      } else if (editingPartner && editingPartner.logoUrl && !editingPartner.logoUrl.startsWith('http://') && !editingPartner.logoUrl.startsWith('https://')) {
        // Préserver le logo existant uploadé si aucun nouveau n'est fourni
        logoUrl = editingPartner.logoUrl;
      }
    } else if (logoType === 'link') {
      logoUrl = formData.get('logoUrl')?.trim() || null;
    } else if (editingPartner && editingPartner.logoUrl) {
      // Préserver le logo existant si aucun nouveau n'est fourni
      logoUrl = editingPartner.logoUrl;
    }

    // Récupérer les coordonnées
    const latitude = formData.get('latitude')?.trim();
    const longitude = formData.get('longitude')?.trim();
    const coordinates = (latitude && longitude) ? [parseFloat(latitude), parseFloat(longitude)] : null;

    const data = {
      name: formData.get('name')?.trim(),
      fullName: formData.get('fullName')?.trim(),
      country: formData.get('country')?.trim(),
      website: formData.get('website')?.trim() || null,
      pi: formData.get('pi')?.trim() || null,
      logoUrl: logoUrl,
      isCoordinator: formData.get('isCoordinator') === 'on',
      logo: formData.get('logo')?.trim() || null,
      logoColor: formData.get('logoColor')?.trim() || null,
      coordinates: coordinates,
      color: formData.get('color')?.trim() || null,
      expertise: formData.get('expertise')?.trim() || null,
      workPackagesLead: workPackagesLead,
      workPackagesContributing: workPackagesContributing
    };

    if (!data.name || !data.fullName || !data.country) {
      showNotification('Les champs Nom court, Nom complet et Pays sont obligatoires.', 'error');
      return;
    }

    try {
      if (editingPartner) {
        await updatePartner(editingPartner.id, data);
        showNotification('Partenaire mis à jour avec succès', 'success');
      } else {
        await createPartner(data);
        showNotification('Partenaire créé avec succès', 'success');
      }
      setShowForm(false);
      setEditingPartner(null);
      setLogoType('link');
      setUploadedLogo(null);
      setUploadedLogoPath(null);
      setLogoError(null);
      setWorkPackagesLead([]);
      setWorkPackagesContributing([]);
      setNewWPId('');
      setNewWPTitle('');
      setNewWPLead('');
      setNewWPType('Lead');
      setEditingWPIndex(null);
      setEditingWPType(null);
      fetchPartners();
    } catch (err) {
      console.error(err);
      showNotification('Erreur lors de l\'enregistrement du partenaire', 'error');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#96ca2d', marginBottom: '20px' }}>
            <i className="fas fa-spinner fa-spin" />
          </div>
          <p style={{ fontSize: '16px', color: '#666' }}>Loading partners...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#ef4444', marginBottom: '20px' }}>
            <i className="fas fa-exclamation-triangle" />
          </div>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={fetchPartners}
            style={{
              padding: '10px 20px',
              background: '#96ca2d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {notification && (
          <div
            style={{
              position: 'fixed',
              top: isMobile ? '10px' : '20px',
              right: isMobile ? '10px' : '20px',
              left: isMobile ? '10px' : 'auto',
              zIndex: 10001,
              minWidth: isMobile ? 'auto' : '300px',
              maxWidth: isMobile ? 'none' : '500px',
              width: isMobile ? 'calc(100% - 20px)' : 'auto',
              background: notification.type === 'success' ? '#10b981' : '#ef4444',
              color: 'white',
              padding: isMobile ? '12px 16px' : '16px 20px',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer'
            }}
            onClick={() => setNotification(null)}
          >
            <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
            <span style={{ flex: 1 }}>{notification.message}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotification(null);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                fontSize: '16px'
              }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
          borderRadius: isMobile ? '12px' : '16px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '16px' : '24px',
          color: 'white',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            flexWrap: 'wrap', 
            gap: isMobile ? '16px' : '20px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ 
              flex: 1, 
              width: isMobile ? '100%' : 'auto',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>Partners Management</h1>
              <p style={{ margin: '8px 0 0 0', opacity: 0.95, fontSize: isMobile ? '14px' : '16px' }}>Manage project partners</p>
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '12px' : '16px', 
                marginTop: isMobile ? '16px' : '24px', 
                flexWrap: 'wrap',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '12px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderRadius: '12px',
                  flex: isMobile ? '1' : 'none',
                  minWidth: isMobile ? '0' : 'auto'
                }}>
                  <i className="fas fa-handshake" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                  <div>
                    <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>{partners.length}</div>
                    <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Total Partners</div>
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '12px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderRadius: '12px',
                  flex: isMobile ? '1' : 'none',
                  minWidth: isMobile ? '0' : 'auto'
                }}>
                  <i className="fas fa-star" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                  <div>
                    <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>
                      {partners.filter(p => p.isCoordinator).length}
                    </div>
                    <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Coordinators</div>
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '12px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderRadius: '12px',
                  flex: isMobile ? '1' : 'none',
                  minWidth: isMobile ? '0' : 'auto'
                }}>
                  <i className="fas fa-globe" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                  <div>
                    <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>
                      {uniqueCountries.length}
                    </div>
                    <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Countries</div>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleNew}
              style={{
                padding: isMobile ? '10px 20px' : '12px 24px',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#96ca2d';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              + New Partner
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: isMobile ? '16px' : '24px',
          border: '1px solid rgba(150, 202, 45, 0.1)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '16px',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <div style={{ flex: '1', minWidth: isMobile ? '100%' : '250px', width: isMobile ? '100%' : 'auto', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ position: 'relative', width: '100%' }}>
              <i className="fas fa-search" style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                fontSize: '14px',
                zIndex: 1
              }} />
              <input
                type="text"
                placeholder="Search by name, full name, or country..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '10px 12px 10px 36px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          <div style={{ minWidth: isMobile ? '100%' : '180px', width: isMobile ? '100%' : 'auto', flex: isMobile ? '1' : 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
            <select
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                maxWidth: '100%',
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'white',
                boxSizing: 'border-box'
              }}
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
          {(searchTerm || countryFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setCountryFilter('');
                setCurrentPage(1);
              }}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Reset Filters
            </button>
          )}
        </div>

        {/* Partners List */}
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid rgba(150, 202, 45, 0.1)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          {currentPartners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
              <p>No partners found.</p>
              <button
                onClick={handleNew}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginTop: '16px'
                }}
              >
                Create First Partner
              </button>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: isMobile ? '16px' : '20px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                {currentPartners.map(partner => (
                  <div key={partner.id} style={{
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: isMobile ? '10px' : '12px',
                    padding: isMobile ? '16px' : '20px',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    {/* Top gradient bar */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '4px',
                      background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)'
                    }} />
                    
                    {/* Card Content */}
                    <div style={{ paddingTop: '4px' }}>
                      {/* Logo and Name Section */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
                        {/* Logo */}
                        {partner.logoUrl && (
                          <div style={{
                            width: '60px',
                            height: '60px',
                            flexShrink: 0,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #e5e7eb'
                          }}>
                            <img
                              src={getFileUrl(partner.logoUrl)}
                              alt={partner.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                padding: '4px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<i class="fas fa-image" style="color: #9ca3af; font-size: 24px;"></i>';
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Name and Coordinator Badge */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                fontWeight: 700,
                                color: '#1f2937',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {partner.name}
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#6b7280',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                marginTop: '2px'
                              }}>
                                {partner.fullName}
                              </div>
                            </div>
                            {partner.isCoordinator && (
                              <span style={{
                                background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}>
                                Coordinator
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Country */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: 600 }}>
                          Country
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#1f2937', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-globe" style={{ color: '#96ca2d', fontSize: '0.75rem' }} />
                          <span>{partner.country}</span>
                        </div>
                      </div>

                      {/* Website */}
                      {partner.website && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: 600 }}>
                            Website
                          </div>
                          <a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.85rem',
                              color: '#2563eb',
                              textDecoration: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            <i className="fas fa-external-link-alt" style={{ fontSize: '0.7rem', flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {partner.website}
                            </span>
                          </a>
                        </div>
                      )}

                      {/* PI */}
                      {partner.pi && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: 600 }}>
                            Principal Investigator (PI)
                          </div>
                          <p style={{
                            fontSize: '0.85rem',
                            color: '#4b5563',
                            margin: 0,
                            lineHeight: '1.5',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {partner.pi}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{
                        marginTop: 'auto',
                        paddingTop: '16px',
                        borderTop: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => handleEdit(partner)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            background: '#f9fafb',
                            color: '#374151',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#96ca2d';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }}
                        >
                          <i className="fas fa-edit" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(partner)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#b91c1c',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fee2e2';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                        >
                          <i className="fas fa-trash" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {filteredPartners.length > itemsPerPage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '8px',
                  marginTop: isMobile ? '20px' : '30px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #96ca2d',
                      background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === 1 ? '#888' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.background = '#7fb800';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.background = '#96ca2d';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    // Calculer les pages à afficher
                    const maxVisible = isMobile ? 3 : 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    
                    // Ajuster si on est près de la fin
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
                    
                    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: isMobile ? '6px 10px' : '8px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${currentPage === page ? '#96ca2d' : '#ddd'}`,
                          background: currentPage === page ? '#96ca2d' : '#fff',
                          color: currentPage === page ? '#fff' : '#333',
                          cursor: 'pointer',
                          fontSize: isMobile ? '12px' : '14px',
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                          minWidth: isMobile ? '32px' : '36px'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== page) {
                            e.currentTarget.style.background = '#f9fafb';
                            e.currentTarget.style.borderColor = '#96ca2d';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== page) {
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {page}
                      </button>
                    ));
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #96ca2d',
                      background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                      fontSize: isMobile ? '12px' : '14px',
                      color: currentPage === totalPages ? '#888' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.currentTarget.style.background = '#7fb800';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.currentTarget.style.background = '#96ca2d';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {partnerToDelete && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
              padding: '16px'
            }}
            onClick={() => setPartnerToDelete(null)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>
                Delete Partner
              </h3>
              <p style={{ margin: '0 0 24px 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Are you sure you want to delete the partner <strong>"{partnerToDelete.name}"</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => setPartnerToDelete(null)}
                  style={{
                    padding: '10px 20px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#96ca2d';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePartner}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#ef4444',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Partner Form Modal */}
        {showForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: isMobile ? '10px' : '20px',
              overflowY: 'auto',
              overflowX: 'hidden',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={() => {
              setShowForm(false);
              setEditingPartner(null);
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: isMobile ? '16px' : '24px',
                maxWidth: isMobile ? 'calc(100% - 20px)' : '750px',
                width: '100%',
                maxHeight: isMobile ? '95vh' : '90vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                boxShadow: '0 30px 100px rgba(0, 0, 0, 0.5)',
                boxSizing: 'border-box',
                animation: 'slideUp 0.3s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Gradient */}
              <div style={{
                background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                padding: isMobile ? '20px' : '28px',
                borderRadius: isMobile ? '16px 16px 0 0' : '24px 24px 0 0',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(150, 202, 45, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                  <h2 style={{ 
                    margin: 0, 
                    fontSize: isMobile ? '20px' : '26px', 
                    fontWeight: 800, 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    letterSpacing: '-0.5px'
                  }}>
                    <i className={`fas ${editingPartner ? 'fa-edit' : 'fa-plus-circle'}`} style={{ fontSize: isMobile ? '20px' : '24px' }} />
                    {editingPartner ? 'Edit' : 'New'} Partner
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingPartner(null);
                    }}
                    style={{
                      width: isMobile ? '36px' : '40px',
                      height: isMobile ? '36px' : '40px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.25)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: isMobile ? '16px' : '18px',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div style={{ 
                padding: isMobile ? '24px 16px' : '36px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-tag" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Short Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingPartner?.name || ''}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-building" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    defaultValue={editingPartner?.fullName || ''}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-globe" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    defaultValue={editingPartner?.country || ''}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-link" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    defaultValue={editingPartner?.website || ''}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-image" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Logo (optional)
                  </label>
                  
                  {/* Toggle between Link and Upload */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoType('link');
                        setUploadedLogo(null);
                        setUploadedLogoPath(null);
                        setLogoError(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: `2px solid ${logoType === 'link' ? '#96ca2d' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        background: logoType === 'link' ? '#f0f9ff' : 'white',
                        color: logoType === 'link' ? '#96ca2d' : '#6b7280',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: logoType === 'link' ? 600 : 500,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-link" style={{ marginRight: '6px' }} />
                      Link
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoType('upload');
                        setLogoError(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: `2px solid ${logoType === 'upload' ? '#96ca2d' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        background: logoType === 'upload' ? '#f0f9ff' : 'white',
                        color: logoType === 'upload' ? '#96ca2d' : '#6b7280',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: logoType === 'upload' ? 600 : 500,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-upload" style={{ marginRight: '6px' }} />
                      Upload Image
                    </button>
                  </div>

                  {/* Link Input */}
                  {logoType === 'link' && (
                    <input
                      type="url"
                      name="logoUrl"
                      defaultValue={editingPartner?.logoUrl && (editingPartner.logoUrl.startsWith('http://') || editingPartner.logoUrl.startsWith('https://')) ? editingPartner.logoUrl : ''}
                      placeholder="https://example.com/logo.png"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#96ca2d';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  )}

                  {/* Upload Input */}
                  {logoType === 'upload' && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '14px',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                          cursor: isUploadingLogo ? 'not-allowed' : 'pointer'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#96ca2d';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                      {isUploadingLogo && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#96ca2d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-spinner fa-spin" />
                          Uploading...
                        </div>
                      )}
                      {uploadedLogoPath && !isUploadingLogo && !logoError && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-check-circle" />
                          Logo uploaded successfully
                        </div>
                      )}
                      {uploadedLogo && !uploadedLogoPath && !isUploadingLogo && !logoError && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-file-image" />
                          {uploadedLogo.name}
                        </div>
                      )}
                      {logoError && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-exclamation-circle" />
                          {logoError}
                        </div>
                      )}
                      {editingPartner?.logoUrl && !editingPartner.logoUrl.startsWith('http://') && !editingPartner.logoUrl.startsWith('https://') && !uploadedLogoPath && !uploadedLogo && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                          Current logo: {editingPartner.logoUrl}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-align-left" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Principal Investigator (PI)
                  </label>
                  <input
                    type="text"
                    name="pi"
                    defaultValue={editingPartner?.pi || ''}
                    placeholder="e.g., Dr. John Smith"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-palette" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Color (Hex)
                  </label>
                  <input
                    type="text"
                    name="color"
                    defaultValue={editingPartner?.color || ''}
                    placeholder="#96ca2d"
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-palette" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Logo Color (Hex)
                  </label>
                  <input
                    type="text"
                    name="logoColor"
                    defaultValue={editingPartner?.logoColor || ''}
                    placeholder="#96ca2d"
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-map-marker-alt" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Coordinates (Latitude, Longitude)
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      defaultValue={editingPartner?.coordinates?.[0] || ''}
                      placeholder="38.7326"
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#96ca2d';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      defaultValue={editingPartner?.coordinates?.[1] || ''}
                      placeholder="35.4893"
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#96ca2d';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-smile" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Logo Emoji (Fallback)
                  </label>
                  <input
                    type="text"
                    name="logo"
                    defaultValue={editingPartner?.logo || ''}
                    placeholder="💧 🔍 🔬 🌱 etc."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <i className="fas fa-lightbulb" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Expertise
                  </label>
                  <textarea
                    name="expertise"
                    defaultValue={editingPartner?.expertise || ''}
                    placeholder="Water reuse strategies, agricultural trials, water management"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Work Packages Lead Section */}
                <div style={{
                  padding: '16px',
                  background: '#f0f9ff',
                  borderRadius: '10px',
                  border: '2px solid #96ca2d30'
                }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                    <i className="fas fa-star" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Work Packages Lead
                  </label>
                  
                  {/* Liste des Work Packages Lead */}
                  <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {workPackagesLead.map((wp, index) => (
                      <div key={index} style={{
                        padding: '10px',
                        background: 'white',
                        borderRadius: '8px',
                        border: editingWPIndex === index && editingWPType === 'Lead' ? '2px solid #96ca2d' : '1px solid #e5e7eb'
                      }}>
                        {editingWPIndex === index && editingWPType === 'Lead' ? (
                          // Mode édition
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              value={wp.id}
                              onChange={(e) => {
                                const updated = [...workPackagesLead];
                                updated[index] = { ...updated[index], id: e.target.value };
                                setWorkPackagesLead(updated);
                              }}
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <input
                              type="text"
                              value={wp.title}
                              onChange={(e) => {
                                const updated = [...workPackagesLead];
                                updated[index] = { ...updated[index], title: e.target.value };
                                setWorkPackagesLead(updated);
                              }}
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <input
                              type="text"
                              value={wp.lead || ''}
                              onChange={(e) => {
                                const updated = [...workPackagesLead];
                                updated[index] = { ...updated[index], lead: e.target.value };
                                setWorkPackagesLead(updated);
                              }}
                              placeholder="Lead (e.g., ERU (Türkiye))"
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(null);
                                  setEditingWPType(null);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(null);
                                  setEditingWPType(null);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#96ca2d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Mode affichage
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '13px' }}>
                                {wp.id} — {wp.title}
                              </div>
                              {wp.lead && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                  Lead: {wp.lead}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(index);
                                  setEditingWPType('Lead');
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#96ca2d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setWorkPackagesLead(workPackagesLead.filter((_, i) => i !== index));
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {workPackagesLead.length === 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                        No lead work packages
                      </div>
                    )}
                  </div>

                  {/* Formulaire pour ajouter un Work Package Lead */}
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        placeholder="WP ID (e.g., WP0, WP1, WP2)"
                        value={newWPId}
                        onChange={(e) => setNewWPId(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Title (e.g., Project Management)"
                        value={newWPTitle}
                        onChange={(e) => setNewWPTitle(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Lead (e.g., ERU (Türkiye))"
                        value={newWPLead}
                        onChange={(e) => setNewWPLead(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (newWPId && newWPTitle) {
                          setWorkPackagesLead([...workPackagesLead, {
                            id: newWPId,
                            title: newWPTitle,
                            lead: newWPLead || '',
                            type: 'Lead'
                          }]);
                          setNewWPId('');
                          setNewWPTitle('');
                          setNewWPLead('');
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: '#96ca2d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '6px' }} />
                      Add Lead Work Package
                    </button>
                  </div>
                </div>

                {/* Work Packages Contributing Section */}
                <div style={{
                  padding: '16px',
                  background: '#fef3c7',
                  borderRadius: '10px',
                  border: '2px solid #fbbf2430'
                }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                    <i className="fas fa-users" style={{ marginRight: '8px', color: '#f59e0b' }} />
                    Work Packages Contributing
                  </label>
                  
                  {/* Liste des Work Packages Contributing */}
                  <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {workPackagesContributing.map((wp, index) => (
                      <div key={index} style={{
                        padding: '10px',
                        background: 'white',
                        borderRadius: '8px',
                        border: editingWPIndex === index && editingWPType === 'Contributing' ? '2px solid #f59e0b' : '1px solid #e5e7eb'
                      }}>
                        {editingWPIndex === index && editingWPType === 'Contributing' ? (
                          // Mode édition
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              value={wp.id}
                              onChange={(e) => {
                                const updated = [...workPackagesContributing];
                                updated[index] = { ...updated[index], id: e.target.value };
                                setWorkPackagesContributing(updated);
                              }}
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <input
                              type="text"
                              value={wp.title}
                              onChange={(e) => {
                                const updated = [...workPackagesContributing];
                                updated[index] = { ...updated[index], title: e.target.value };
                                setWorkPackagesContributing(updated);
                              }}
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <input
                              type="text"
                              value={wp.lead || ''}
                              onChange={(e) => {
                                const updated = [...workPackagesContributing];
                                updated[index] = { ...updated[index], lead: e.target.value };
                                setWorkPackagesContributing(updated);
                              }}
                              placeholder="Lead (e.g., ERU (Türkiye))"
                              style={{
                                padding: '6px 10px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '13px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(null);
                                  setEditingWPType(null);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(null);
                                  setEditingWPType(null);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Mode affichage
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '13px' }}>
                                {wp.id} — {wp.title}
                              </div>
                              {wp.lead && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                  Lead: {wp.lead}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWPIndex(index);
                                  setEditingWPType('Contributing');
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setWorkPackagesContributing(workPackagesContributing.filter((_, i) => i !== index));
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {workPackagesContributing.length === 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                        No contributing work packages
                      </div>
                    )}
                  </div>

                  {/* Formulaire pour ajouter un Work Package Contributing */}
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        placeholder="WP ID (e.g., WP0, WP1, WP2)"
                        value={newWPId}
                        onChange={(e) => setNewWPId(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Title (e.g., Project Management)"
                        value={newWPTitle}
                        onChange={(e) => setNewWPTitle(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Lead (e.g., ERU (Türkiye))"
                        value={newWPLead}
                        onChange={(e) => setNewWPLead(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (newWPId && newWPTitle) {
                          setWorkPackagesContributing([...workPackagesContributing, {
                            id: newWPId,
                            title: newWPTitle,
                            lead: newWPLead || '',
                            type: 'Contributing partner'
                          }]);
                          setNewWPId('');
                          setNewWPTitle('');
                          setNewWPLead('');
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '6px' }} />
                      Add Contributing Work Package
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '10px',
                  border: '2px solid #e5e7eb'
                }}>
                  <input
                    type="checkbox"
                    id="isCoordinator"
                    name="isCoordinator"
                    defaultChecked={!!editingPartner?.isCoordinator}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#96ca2d'
                    }}
                  />
                  <label htmlFor="isCoordinator" style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    cursor: 'pointer',
                    flex: 1
                  }}>
                    <i className="fas fa-star" style={{ marginRight: '8px', color: '#96ca2d' }} />
                    Coordinator Partner
                  </label>
                </div>

                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  paddingTop: '20px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPartner(null);
                    }}
                    style={{
                      padding: isMobile ? '12px 20px' : '12px 24px',
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      background: 'white',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#96ca2d';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: isMobile ? '12px 20px' : '12px 24px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: isMobile ? '100%' : 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <i className="fas fa-save" />
                    {editingPartner ? 'Update Partner' : 'Create Partner'}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminPartners;
